import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import mapImage from '../../assets/images/map.png';
import parkingImage from '../../assets/images/parking.png';
import './ParkingOptions.css';

const HeroSection = styled.section`
  position: relative;
  height: 60vh;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url(${mapImage}) center center/cover no-repeat;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(37, 37, 37, 0.7); /* #A55F60 overlay */
    z-index: 1;
  }

  @media (max-width: 768px) {
    height: 30vh;
    min-height: 250px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const ParkingOptionsSection = styled.section`
  padding: 4rem 0;
  background: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const SectionTitle = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  text-align: center;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: #64748b;
  text-align: center;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const AccentLine = styled(motion.div)`
  width: 80px;
  height: 4px;
  background: #FF0707;
  margin: 0 auto 4rem;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const OptionCard = styled(motion.div)`
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: #FF0707;
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const OptionIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #FF0707;
  border-radius: 50%;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  font-weight: bold;
`;

const OptionTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const OptionDescription = styled.p`
  font-size: 1.1rem;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const OptionFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const FeatureItem = styled.li`
  font-size: 1rem;
  color: #475569;
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
  position: relative;

  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: #FF0707;
    font-weight: bold;
  }
`;

const LearnMoreButton = styled.button`
  background: #FF0707;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 2rem;

  &:hover {
    background: #d60000;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 7, 7, 0.3);
  }
`;

const ContactSection = styled(motion.div)`
  background: #7F1B1C;
  padding: 3rem;
  margin-top: 4rem;
  margin-left: calc(-20px - 2vw);
  margin-right: calc(-20px - 2vw);
  text-align: center;
  border-radius: 12px;

  @media (max-width: 768px) {
    padding: 2rem;
    margin-left: -15px;
    margin-right: -15px;
  }
`;

const ContactTitle = styled.h3`
  color: white;
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ContactText = styled.p`
  color: #e2e8f0;
  font-size: 1.1rem;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ContactButton = styled.button`
  background: linear-gradient(135deg, #2d1b1b 0%, #FF0707 100%);
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 7, 7, 0.3);
  }
`;

const RatesSection = styled.section`
  padding: 4rem 0;
  background: #f8f9fa;
`;

const RatesTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  margin-bottom: 0.5rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: #FF0707;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const RatesSubtitle = styled(motion.p)`
  font-size: 1.1rem;
  color: #64748b;
  text-align: center;
  margin-bottom: 3rem;
`;

const RatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RateCard = styled(motion.div)`
  background: white;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #C31321;
  }
`;

const RatePrice = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #FF0707;
  margin-bottom: 1rem;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;

  .currency {
    font-size: 1.5rem;
    font-weight: 600;
    align-self: flex-start;
    margin-top: 0.5rem;
    color: #C31321;
  }

  .number {
    font-size: 3rem;
    font-weight: 700;
    color: #C31321;
  }

  .unit {
    font-size: 1.25rem;
    font-weight: 600;
    color: #C31321;
  }
`;

const RateClass = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1.5rem;
`;

const LocationsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const LocationItem = styled.li`
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 0.5rem;
  line-height: 1.4;
`;

const LongStaySection = styled.section`
  position: relative;
  height: 80vh;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url(${parkingImage}) center center/cover no-repeat;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 1;
  }
`;

const LongStayContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
`;

const LongStayTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 2px;
    background: white;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const LongStaySubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: white;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ContactUsButton = styled(motion.button)`
  background: linear-gradient(135deg, #FF0707 0%, #C31321 100%);
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 7, 7, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 7, 7, 0.4);
  }
`;

const OffStreetSection = styled.section`
  padding: 4rem 0;
  background: #f8f9fa;
`;

const OffStreetContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const OffStreetTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  color: #333;
  text-align: center;
  margin-bottom: 1rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background: #FF0707;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const OffStreetSubtitle = styled(motion.h3)`
  font-size: 1.5rem;
  font-weight: 600;
  color: #666;
  text-align: center;
  margin-bottom: 3rem;
`;

const ParkingTable = styled(motion.div)`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  margin: 0 auto;
`;

const TableHeader = styled.div`
  background: #C31321;
  color: white;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 1rem 2rem;
  font-weight: 700;
  font-size: 1.1rem;
`;

const TableHeaderCell = styled.div`
  text-align: ${props => props.align || 'left'};
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 1rem 2rem;
  background: ${props => props.isEven ? '#f8f9fa' : 'white'};
  border-bottom: 1px solid #e0e0e0;
  font-size: 1rem;
  color: #333;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  text-align: ${props => props.align || 'left'};
`;

const ParkingOptions = () => {
  const options = [
    {
      icon: "🚗",
      title: "On-Street Parking",
      description: "Convenient parking options available throughout the city with different pricing tiers based on location and demand.",
      features: [
        "Class A: Premium locations - N$ 8/hour",
        "Class B: Standard locations - N$ 5/hour", 
        "Class C: Economy locations - N$ 3/hour",
        "Easy payment via mobile app",
        "Real-time availability updates"
      ]
    },
    {
      icon: "🏢",
      title: "Off-Street Parking",
      description: "Secure parking facilities at multiple locations across the city with competitive monthly rates and flexible contracts.",
      features: [
        "3 dedicated parking facilities",
        "Starting from N$ 2/hour",
        "Monthly contract options",
        "24/7 security monitoring",
        "Covered parking available"
      ]
    }
  ];

  const onStreetRates = [
    {
      class: "Class A",
      price: "N$ 8",
      unit: "/hour",
      locations: [
        "Independence Avenue (F.C - J.M.)",
        "Judge J.P Karuaihe Street (F.C - J.M.)",
        "Frans Indongo Street (M.N.A. - W.L.)",
        "Fidel Castro Street (I.A - W.L.)",
        "Werner List Street (F.C - J.M.)",
        "Daniel Munamava Street",
        "Kasino Street"
      ]
    },
    {
      class: "Class B",
      price: "N$ 5",
      unit: "/hour",
      locations: [
        "Independence Avenue (S.N - F.C.)",
        "Independence Avenue (B.H - J.M.)",
        "Daniel Munamava Street (J.J.P. - R.M)",
        "Prof. Murumba Street (R.M. - M.N.A.)",
        "Robert Mugabe Avenue (P.M.- S.N.)",
        "Rev Michael Scott Street (F.C.- S.N.)",
        "John Meinert Street (M.N.A. - R.M.)",
        "Fidel Castro Street (W.L. - M.N.A)",
        "Werner List Street (J.M. - B.H.)",
        "Tal Street (M.N. - S.N)",
        "Moltke Street",
        "Kasch Street"
      ]
    },
    {
      class: "Class C",
      price: "N$ 3",
      unit: "/hour",
      locations: [
        "Rev Michael Scott Street (S.N. - GAR)",
        "Independence Avenue (F.C - A.B.M.)",
        "Rehobother Road (A.B.M. - MCA)",
        "Jan Jonker Street (A.B.M. - J.N.)",
        "Doctor Agostinho Neta Road",
        "Doctor A.B. May Street",
        "Tal Street (RHB - W.L.)",
        "Garten Street",
        "Venning Street",
        "Losen Street"
      ]
    }
  ];

  const offStreetRates = [
    { time: '0 - 20 min', tariff: '2.00' },
    { time: '21 min - 1 hour', tariff: '5.00' },
    { time: '2 hour', tariff: '7.00' },
    { time: '3 hours', tariff: '9.00' },
    { time: '4 hours', tariff: '12.00' },
    { time: '5 hours', tariff: '14.00' },
    { time: '6 hours', tariff: '16.50' },
    { time: '7 hours', tariff: '31.50' },
    { time: '8 hours', tariff: '42.00' },
    { time: '9 hours', tariff: '47.00' },
    { time: '10 hours', tariff: '52.50' },
    { time: '11 hours', tariff: '58.00' },
    { time: '12 hours', tariff: '68.00' },
    { time: 'Night time parking - whether overnight or a portion thereof per day', tariff: '73.50' },
    { time: 'Lost Ticket', tariff: '52.55' }
  ];

  return (
    <>
      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Parking Options
          </HeroTitle>
        </HeroContent>
      </HeroSection>
      
             

       <RatesSection>
         <Container>
           <RatesTitle
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8 }}
           >
             On-Street Parking Options and Rates
           </RatesTitle>
           
           <RatesSubtitle
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8, delay: 0.2 }}
           >
             Choose from our three-tier pricing system based on location and demand
           </RatesSubtitle>

           <RatesGrid
             as={motion.div}
             initial={{ opacity: 0, y: 50 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8, delay: 0.4 }}
           >
             {onStreetRates.map((rate, index) => (
               <RateCard
                 key={index}
                 initial={{ opacity: 0, y: 30 }}
                 whileInView={{ opacity: 1, y: 0 }}
                 viewport={{ once: true }}
                 transition={{ duration: 0.6, delay: 0.6 + index * 0.2 }}
               >
                 <RatePrice>
                   <span className="currency">N$</span>
                   <span className="number">{rate.price.split(' ')[1]}</span>
                   <span className="unit">{rate.unit}</span>
                 </RatePrice>
                 <RateClass>{rate.class}</RateClass>
                 <LocationsList>
                   {rate.locations.map((location, locationIndex) => (
                     <LocationItem key={locationIndex}>{location}</LocationItem>
                   ))}
                 </LocationsList>
               </RateCard>
             ))}
           </RatesGrid>
         </Container>
       </RatesSection>

       <LongStaySection>
         <LongStayContent>
           <LongStayTitle
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8 }}
           >
             Long Stay Parking
           </LongStayTitle>
           
           <LongStaySubtitle
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8, delay: 0.2 }}
           >
             Interested in long stay parking, contact the KeyPlot Customer Care Centre for further information.
           </LongStaySubtitle>

           <ContactUsButton
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8, delay: 0.4 }}
           >
             Contact Us
           </ContactUsButton>
         </LongStayContent>
       </LongStaySection>

       <OffStreetSection>
         <OffStreetContainer>
           <OffStreetTitle
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8 }}
           >
             Off-Street Parking Options and Rates
           </OffStreetTitle>
           
           <OffStreetSubtitle
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8, delay: 0.2 }}
           >
             City Parkade - Town Square
           </OffStreetSubtitle>

           <ParkingTable
             initial={{ opacity: 0, y: 50 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8, delay: 0.4 }}
           >
             <TableHeader>
               <TableHeaderCell>Time</TableHeaderCell>
               <TableHeaderCell align="right">Parking Tariff (N$)</TableHeaderCell>
             </TableHeader>
             
             {offStreetRates.map((rate, index) => (
               <TableRow key={index} isEven={index % 2 === 1}>
                 <TableCell>{rate.time}</TableCell>
                 <TableCell align="right">{rate.tariff}</TableCell>
               </TableRow>
             ))}
           </ParkingTable>
         </OffStreetContainer>
       </OffStreetSection>


    </>
  );
};

export default ParkingOptions; 